import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { DEFAULT_ROLES, DEFAULT_CERT_TYPES } from '@cls/shared';
import { SearchableSelect } from '../primitives/SearchableSelect.js';
import { useReferenceList } from '../../features/reference/useReferenceList.js';

interface Props {
  onClose: () => void;
  initialRole?: string;
  initialCategory?: 'mandatory' | 'beneficial' | 'other';
}

interface RoleReq { role: string; mandatory: string[]; beneficial: string[]; other: string[]; displayOrder?: number; }
interface CertTypeRow { id: string; value: string; }

type Cat = 'mandatory' | 'beneficial' | 'other';

export function AddRoleReqModal({ onClose, initialRole, initialCategory }: Props) {
  const qc = useQueryClient();
  const { values: roles } = useReferenceList('roles', DEFAULT_ROLES);
  const [role, setRole] = useState<string>(initialRole ?? '');
  const [mandatory, setMandatory] = useState<string[]>([]);
  const [beneficial, setBeneficial] = useState<string[]>([]);
  const [other, setOther] = useState<string[]>([]);
  const [mCustom, setMCustom] = useState('');
  const [bCustom, setBCustom] = useState('');
  const [oCustom, setOCustom] = useState('');
  const lastSyncedRole = useRef<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: existing } = useQuery({
    queryKey: ['training', 'role-requirement', role],
    queryFn: () => api.get<RoleReq>(`/training/role-requirements/${encodeURIComponent(role)}`).then((r) => r.data).catch(() => null),
    enabled: !!role,
  });

  const { data: certTypeRows = [] } = useQuery({
    queryKey: ['training', 'cert-types'],
    queryFn: () => api.get<CertTypeRow[]>('/training/cert-types').then((r) => r.data).catch(() => [] as CertTypeRow[]),
  });
  const { values: settingsCertTypes } = useReferenceList('cert-types', DEFAULT_CERT_TYPES);

  const allCertTypes = useMemo(() => {
    // Settings DB list is the source of truth; fold in any types referenced by
    // existing matrix rows so legacy data stays visible.
    const set = new Set<string>([...settingsCertTypes, ...certTypeRows.map((c) => c.value)]);
    return Array.from(set).sort();
  }, [certTypeRows, settingsCertTypes]);

  // When the role's existing requirements load, pre-populate the three columns.
  // Only sync once per role change so we don't clobber user edits in progress.
  useEffect(() => {
    if (!role) return;
    if (lastSyncedRole.current === role) return;
    if (existing === undefined) return; // still loading
    const known = new Set(allCertTypes);
    const m = existing?.mandatory ?? [];
    const b = existing?.beneficial ?? [];
    const o = existing?.other ?? [];
    setMandatory(m.filter((c) => known.has(c)));
    setBeneficial(b.filter((c) => known.has(c)));
    setOther(o.filter((c) => known.has(c)));
    setMCustom(m.filter((c) => !known.has(c)).join(', '));
    setBCustom(b.filter((c) => !known.has(c)).join(', '));
    setOCustom(o.filter((c) => !known.has(c)).join(', '));
    lastSyncedRole.current = role;
  }, [role, existing, allCertTypes]);

  const save = useMutation({
    mutationFn: async () => {
      const splitCustom = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
      const body = {
        role,
        mandatory: Array.from(new Set([...mandatory, ...splitCustom(mCustom)])),
        beneficial: Array.from(new Set([...beneficial, ...splitCustom(bCustom)])),
        other: Array.from(new Set([...other, ...splitCustom(oCustom)])),
      };
      return api.put('/training/role-requirements', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['training', 'role-requirements'] });
      qc.invalidateQueries({ queryKey: ['training', 'role-requirement', role] });
      toast.success('Requirement saved', role);
      onClose();
    },
    onError: () => toast.error('Save failed'),
  });

  const toggle = (list: string[], setList: (v: string[]) => void, v: string) => {
    if (list.includes(v)) setList(list.filter((x) => x !== v));
    else setList([...list, v]);
  };

  const roleOptions = roles.map((r) => ({ value: r, label: r }));
  const dirty =
    mandatory.length || beneficial.length || other.length ||
    mCustom.trim() || bCustom.trim() || oCustom.trim();

  const columns: Array<{ cat: Cat; color: string; list: string[]; setList: (v: string[]) => void; custom: string; setCustom: (v: string) => void }> = [
    { cat: 'mandatory', color: 'var(--accent)', list: mandatory, setList: setMandatory, custom: mCustom, setCustom: setMCustom },
    { cat: 'beneficial', color: 'var(--info)', list: beneficial, setList: setBeneficial, custom: bCustom, setCustom: setBCustom },
    { cat: 'other', color: 'var(--faint)', list: other, setList: setOther, custom: oCustom, setCustom: setOCustom },
  ];
  const focusedCat: Cat = (initialCategory ?? 'mandatory') as Cat;

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 760, maxWidth: '98vw', maxHeight: '92vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-hdr" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <div className="modal-title" id="arr-title">Set Role Requirements</div>
            <div className="modal-sub" style={{ marginTop: 2 }}>Pick a role, tick certificates per category. Custom certs can be added comma-separated.</div>
          </div>
          <button className="modal-close close-btn" onClick={onClose}>✕</button>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label className="form-label">Role <span style={{ color: '#ef4444' }}>*</span></label>
          <SearchableSelect id="arr-role" options={roleOptions} value={role} onChange={setRole} placeholder="— Select role —" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
          {columns.map((cfg) => {
            const isFocused = cfg.cat === focusedCat;
            return (
              <div key={cfg.cat} style={isFocused ? { outline: '1px dashed var(--border)', outlineOffset: 4, borderRadius: 8 } : undefined}>
                <div style={{ fontSize: 9, color: cfg.color, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 5 }}>
                  {cfg.cat} ({cfg.list.length})
                </div>
                <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: 7, minHeight: 140, maxHeight: 280, overflowY: 'auto' }}>
                  {allCertTypes.length === 0 ? (
                    <div style={{ fontSize: 10, color: 'var(--faint)', padding: 4 }}>No certificate types defined yet</div>
                  ) : allCertTypes.map((t) => (
                    <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 10, padding: '3px 0', borderBottom: '1px solid rgba(30,42,64,.2)' }}>
                      <input
                        type="checkbox"
                        name={`arr-${cfg.cat}`}
                        value={t}
                        checked={cfg.list.includes(t)}
                        onChange={() => toggle(cfg.list, cfg.setList, t)}
                        style={{ accentColor: 'var(--accent)', flexShrink: 0 }}
                      />
                      <span>{t}</span>
                    </label>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder="Add unlisted cert (comma sep)…"
                  className="form-input"
                  style={{ marginTop: 5, fontSize: 11 }}
                  value={cfg.custom}
                  onChange={(e) => cfg.setCustom(e.target.value)}
                />
              </div>
            );
          })}
        </div>

        {existing && ((existing.mandatory?.length ?? 0) + (existing.beneficial?.length ?? 0) + (existing.other?.length ?? 0)) > 0 ? (
          <div id="arr-existing" style={{ marginBottom: 12, fontSize: 11, color: 'var(--muted)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '7px 10px' }}>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--muted)', marginBottom: 4 }}>Currently saved for this role</div>
            {existing.mandatory.length ? <div><span style={{ color: 'var(--accent)' }}>Mandatory:</span> {existing.mandatory.join(', ')}</div> : null}
            {existing.beneficial.length ? <div><span style={{ color: 'var(--info)' }}>Beneficial:</span> {existing.beneficial.join(', ')}</div> : null}
            {existing.other.length ? <div><span style={{ color: 'var(--faint)' }}>Other:</span> {existing.other.join(', ')}</div> : null}
          </div>
        ) : null}

        <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 12 }}>
          Saves all three columns to <strong id="arr-role-label">{role || '—'}</strong>. Replaces any existing requirements for this role. Updates all workers with this role immediately.
        </div>

        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending || !role || !dirty}>
            {save.isPending ? 'Saving…' : '✓ Save Requirements'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
